import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useLocation } from 'react-router-dom';
//import './CustomerReturnsList.css';
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
    Row,
    Spinner
} from 'reactstrap';
import {
    FaCheckCircle,
    FaEdit,
    FaFilePdf,
    FaFilter,
    FaMoneyBillWave,
    FaPlus,
    FaSearch,
    FaTrash,
    FaUndo,
    FaUser
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { debounce } from 'lodash';
import { formatPrice } from '../../helpers/utility';
import FournisseurRetourForm from './FournisseurRetourForm';
import "jspdf-autotable";
import { retourFournisseurActions } from '../../sagas/retourFournisseurSlice';
import { generateDoc, generateRetourFournisseur } from '../../helpers/generateDocAchat';
import { supplierActions } from '../../sagas/supplierSlice';
import Select from "react-select";
import './FournisseurRetourList.css';

const FournisseurRetourList = () => {
    const dispatch = useDispatch();
    const { retourFournisseur, loading } = useSelector((state) => state.retourFournisseur);
    const [showModal, setShowModal] = useState(false);
    const { t } = useTranslation();

    const [selectedReturn, setSelectedReturn] = useState(null);
    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const searchInputRef = useRef(null);
    const { company } = useSelector(state => state.auth);
    const { suppliers, loading: suppliersLoading } = useSelector(state => state.suppliers);

    // Filter states
    const [filters, setFilters] = useState({
        fournisseurId: '',
        dateFrom: formatDate(todayDate),
        dateTo: formatDate(tomorrowDate),
        query: ''
    });

    const navigate = useNavigate();
    const location = useLocation();

    const getSourceDocumentNumber = (returnItem) => {
        if (returnItem.documentType === 'BR') {
            return returnItem.brOrigineNumero
                || returnItem.bonReceptionOrigineNumero
                || returnItem.sourceBonReceptionNumero
                || returnItem.sourceBonReception?.numero
                || 'BR non renseigné';
        }
        if (returnItem.documentType === 'FACTURE') {
            return returnItem.factureOrigineNumero
                || returnItem.sourceFactureNumero
                || returnItem.sourceFacture?.numero
                || 'Facture non renseignée';
        }
        return '-';
    };

    const getRetourFormPath = () => {
        return '/purchases-module/returns/new';
    };

    const handleEdit = (returnItem) => {
        const path = "/purchases-module/returns/edit"
        navigate(path, { state: { returnItem } });
    };

    const handleSupplierChange = (value) => {
        setFilters(f => ({ ...f, fournisseurId: value }));
    };

    const fournisseurOptions = suppliers?.map(sup => ({
        solde: sup.solde,
        totalDebit: sup.totalDebit,
        totalCredit: sup.totalCredit,
        value: sup.id,
        label: sup.typeFournisseur === 'PARTICULIER' ? `${sup.nom} ${sup.prenom} (${sup.code})` : `${sup.entreprise}(${sup.code})`,
    })) ?? [];

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [retourFournisseur]);

    useEffect(() => {
        dispatch(retourFournisseurActions.list({
            ...filters,
        }));
    }, [filters.fournisseurId, filters.dateFrom, filters.dateTo]);

    useEffect(() => {
        if (suppliers.length === 0) {
            // Fetch suppliers for supplier report
            const fetchSuppliers = async () => {
                dispatch(supplierActions.search({
                    query: ""
                }));
            };

            fetchSuppliers();
        }
    }, []);

    const debouncedSearch = () => {
        dispatch(retourFournisseurActions.list({
            ...filters,
        }));

    }

    const clearFilters = () => {
        setFilters({
            query: '',
            clientId: null,
            dateFrom: formatDate(todayDate),
            dateTo: formatDate(tomorrowDate)
        });
    };

    const handlePrintChoice = (returnItem) => {
        Swal.fire({
            title: 'Impression',
            text: 'Choisissez le format',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: '🧾 Format A5',
            cancelButtonText: '📄 Format A4',
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                generateThermalRetourTicket(returnItem);
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                generateA4RetourPdf(returnItem);
            }
        });
    };

    const generateThermalRetourTicket = (returnItem) => {
        const pdfDoc = generateRetourFournisseur(returnItem, {
            company: company,
            format: "A5",
            showPrices: true,
        });

        pdfDoc.output("dataurlnewwindow");
    };

    const generateA4RetourPdf = (returnItem) => {
        const pdfDoc = generateRetourFournisseur(returnItem, {
            company: company,
            format: "A4",
            showPrices: true,
        });
        pdfDoc.output("dataurlnewwindow");
    };




    const handleCloseModal = () => {
        setShowModal(false);
        if (selectedReturn) {
            setSelectedReturn(null);
        }

        const payload = {
            ...filters
        };
        dispatch(retourFournisseurActions.list(payload));
    };

    const handleValidateReturn = (returnItem) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('customerReturns.confirmValidate'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (!result.isConfirmed) return;
            let payload = {
                id: returnItem.id,
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('common.updated'),
                        text: t('customerReturns.validatedSuccess'),
                    });
                    // Refresh current page
                    const refreshPayload = {
                        ...filters
                    };
                    dispatch(retourFournisseurActions.list(refreshPayload));
                },
            };
            dispatch(retourFournisseurActions.validate(payload));
        });
    }

    const handleCancelReturn = (returnItem) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('customerReturns.confirmDelete'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (!result.isConfirmed) return;
            let payload = {
                id: returnItem.id,
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('common.deleted'),
                        text: t('customerReturns.deletedSuccess'),
                    });
                    // Refresh current page
                    const refreshPayload = {
                        ...filters
                    };
                    dispatch(retourFournisseurActions.list(refreshPayload));
                },
            };
            dispatch(retourFournisseurActions.delete(payload));
        });
    }

    return (
        <Container fluid>
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaUndo className="text-primary me-2" />
                                {t('retourFournisseur.list')}
                            </h4>
                            <div className="d-flex align-items-center">
                                <Button color="primary" onClick={() => navigate(getRetourFormPath())}>
                                    <FaPlus className="me-2" /> {t('retourFournisseur.new')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm">
                <CardBody>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <div className="filter-container mb-4">
                                <Row className="mb-3 g-2 align-items-end">
                                    <Col lg={3} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label className="mb-1">{t('reglement.search')}</Label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light">
                                                    <FaSearch />
                                                </span>
                                                <Input
                                                    type="text"
                                                    innerRef={searchInputRef}
                                                    placeholder={t('retourFournisseur.searchPlaceholder')}
                                                    value={filters.query}
                                                    onChange={e => {
                                                        const value = e.target.value.toUpperCase();
                                                        setFilters(prevFilters => ({
                                                            ...prevFilters,
                                                            query: value
                                                        }));
                                                    }}
                                                    className="text-uppercase"
                                                    style={{ textTransform: 'uppercase' }}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') {
                                                            debouncedSearch(e.target.value.toUpperCase());
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label for="supplierSelect" className="form-label">
                                                <FaUser className="me-1" /> Fournisseur
                                            </Label>
                                            <Select
                                                id="supplierSelect"
                                                options={fournisseurOptions}
                                                isLoading={suppliersLoading}
                                                value={fournisseurOptions.find(option => option.value === filters.fournisseurId)}
                                                onChange={(option) => handleSupplierChange(option?.value)}
                                                placeholder="Sélectionner..."
                                                isClearable
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                styles={{
                                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="dateFrom" className="mb-1">Date début</Label>
                                            <Input
                                                type="date"
                                                id="dateFrom"
                                                value={filters.dateFrom}
                                                onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="dateTo" className="mb-1">Date fin</Label>
                                            <Input
                                                type="date"
                                                id="dateTo"
                                                value={filters.dateTo}
                                                onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                                        <Button
                                            color="secondary"
                                            onClick={clearFilters}
                                            title={t('common.clearFilters')}
                                            className="mb-3"
                                            style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <FaFilter />
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-hover table-bordered supplier-return-table">
                                    <thead className="thead-light">
                                        <tr>
                                            <th>{t('retourFournisseur.numero')} / date</th>
                                            <th>{t('retourFournisseur.fournisseur')}</th>
                                            <th>Document d'origine</th>
                                            <th>Détail du retour</th>
                                            <th className="text-end">Impact financier</th>
                                            <th className="align-middle text-center">{t('retourFournisseur.status')}</th>
                                            <th className="align-middle text-center">{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {retourFournisseur.map((returnItem) => (
                                            <tr key={returnItem.id}>
                                                <td className="align-middle">
                                                    <strong className="document-main-ref">{returnItem.numeroRetour}</strong>
                                                    <div className="return-secondary">{returnItem.dateRetour || 'N/A'}</div>
                                                    <div className="return-secondary">Par {returnItem.createdBy || '-'}</div>
                                                </td>
                                                <td className="align-middle">
                                                    <strong>{returnItem.fournisseurNom}</strong>
                                                    <div className="return-secondary">{returnItem.fournisseurCode || '-'}</div>
                                                    <div className="return-secondary">{returnItem.entrepotNom || '-'}</div>
                                                </td>
                                                <td className="align-middle">
                                                    <span className={`badge ${returnItem.documentType === 'BR' ? 'bg-primary' : 'bg-info text-dark'}`}>{returnItem.documentType === 'BR' ? 'Bon de réception' : returnItem.documentType === 'FACTURE' ? 'Facture' : 'Sans document'}</span>
                                                    <div className="mt-1 fw-bold">{getSourceDocumentNumber(returnItem)}</div>
                                                    <div className="return-secondary">{returnItem.transporteurName || '-'} {returnItem.transporteurMatricule ? `(${returnItem.transporteurMatricule})` : ''}</div>
                                                </td>
                                                <td className="align-middle">
                                                    <strong>{(returnItem.lignes || []).reduce((sum, line) => sum + Number(line.quantiteRetournee || 0), 0)} unité(s) · {(returnItem.lignes || []).length} ligne(s)</strong>
                                                    {(returnItem.lignes || []).slice(0, 2).map((line, index) => <div className="return-secondary" key={line.id || `${returnItem.id}-${index}`}>{line.articleNom || line.articleCode || 'Article'} : {line.quantiteRetournee} {line.unite || ''}</div>)}
                                                    {(returnItem.lignes || []).length > 2 && <div className="return-secondary">+ {(returnItem.lignes || []).length - 2} autre(s) ligne(s)</div>}
                                                    {returnItem.motifRetour && <div className="return-motif" title={returnItem.motifRetour}>{returnItem.motifRetour}</div>}
                                                </td>
                                                <td className="align-middle text-end"><strong className="fs-6 text-danger">− {formatPrice(returnItem.montantTTC, false)}</strong><div className="mt-1"><span className="badge bg-light text-dark border">{returnItem.modeRemboursement || '-'}</span></div></td>
                                                <td className="align-middle text-center">
                                                    {returnItem.statut === 'ANNULE' && (
                                                        <span className="badge bg-danger">{t('customerReturns.annule')}</span>
                                                    )}
                                                    {returnItem.statut === 'VALIDE' && (
                                                        <span className="badge bg-success">{t('customerReturns.valide')}</span>
                                                    )}
                                                    {returnItem.statut === 'BROUILLON' && (
                                                        <span className="badge bg-secondary">{t('customerReturns.brouillon')}</span>
                                                    )}

                                                </td>

                                                <td className="align-middle text-center">
                                                    <div className="btn-group">
                                                        <Button
                                                            color="secondary"
                                                            title={t('customerReturns.generatePdf')}
                                                            onClick={() => handlePrintChoice(returnItem)}
                                                            className="me-1"
                                                            size="sm"
                                                        >
                                                            <FaFilePdf />
                                                        </Button>
                                                        {returnItem.statut === 'BROUILLON' && (
                                                            <Button
                                                                color="success"
                                                                title={t('customerReturns.validate')}
                                                                onClick={() => handleValidateReturn(returnItem)}
                                                                className="me-1"
                                                                size="sm"
                                                            >
                                                                <FaCheckCircle />
                                                            </Button>
                                                        )}

                                                        <Button
                                                            color="warning"
                                                            title={t('customerReturns.edit')}
                                                            onClick={() => handleEdit(returnItem)}
                                                            size="sm"
                                                            className="me-1"
                                                        >
                                                            <FaEdit />
                                                        </Button>

                                                        {returnItem.statut === 'BROUILLON' && (
                                                            <Button
                                                                color="danger"
                                                                title={t('customerReturns.delete')}
                                                                onClick={() => handleCancelReturn(returnItem)}
                                                                className="me-1"
                                                                size="sm"
                                                            >
                                                                <FaTrash />
                                                            </Button>
                                                        )}

                                                        {returnItem.statut === 'COMPTABILISE' &&
                                                            returnItem.modeRemboursement === 'REMBOURSEMENT' &&
                                                            !returnItem.estRembourse && (
                                                                <Button
                                                                    color="info"
                                                                    title={t('customerReturns.processRefund')}
                                                                    onClick={() => navigate(`/ventes/remboursements/new?retourId=${returnItem.id}`)}
                                                                    className="me-1"
                                                                    size="sm"
                                                                >
                                                                    <FaMoneyBillWave />
                                                                </Button>
                                                            )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {
                                            retourFournisseur.length === 0 && (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4">
                                                        <div className="text-muted">
                                                            {
                                                                t('customerReturns.noReturns')
                                                            }
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-light fw-bold">
                                            <td colSpan="4" className="text-end">{t('common.totals')}</td>
                                            <td className="text-end text-danger">− {formatPrice(retourFournisseur.reduce((sum, ret) => sum + (ret.montantTTC || 0), 0), false)}</td>
                                            <td colSpan="2"></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </>
                    )}
                </CardBody>
            </Card>

            {/* Modal for Customer Return Form */}
            <Modal isOpen={showModal} toggle={handleCloseModal} size="xl"
                className="modal-fullscreen-lg-down"
                style={{ maxWidth: '95%', width: '95%', margin: '10px auto' }}
            >
                <ModalHeader toggle={handleCloseModal}>
                    {selectedReturn ? `${t('customerReturns.edit')} N° ${selectedReturn.numeroRetour}` : t('customerReturns.new')}
                </ModalHeader>
                <ModalBody>
                    <FournisseurRetourForm retour={selectedReturn} onClose={handleCloseModal} />
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default FournisseurRetourList;

